import { Box, Typography, Menu, MenuItem, Tooltip, alpha, useMediaQuery } from "@mui/material";
import { useRecordContext, useResourceContext, useNotify, useRefresh, RaRecord } from "react-admin";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { ReactNode, useState, MouseEvent } from "react";
import { GhostButton } from "./GhostButton";
import { depluralize, formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { getStatusColor, StatusColor } from "@/utils/helpers/getStatusColor";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  ResourceName,
  ValidAny,
  PolicyStatus,
  ClientStatus,
  CommonStatus,
  ReimbursementStatus,
  StoplossClaimStatus,
  AlertType,
  PartnerStatus,
} from "@/types";
import dataProvider from "@/providers/dataProvider/dataProvider";
import { StatusChip } from "./fields/StatusChipField";
import { ColorType } from "@/types/shared/uiComponents";
import { useAppTheme } from "@/hooks/useAppTheme";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";

/**
 * Safely accesses nested object properties using dot notation.
 */
const getNestedValue = (obj: ValidAny, path: string, fallback: string = "-"): string | number => {
  if (!obj || !path || path === "") return fallback;
  return path.split(".").reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : null), obj) ?? fallback;
};

/** Maps resource names to their available status options for the status dropdown */
const STATUS_OPTIONS: Record<string, readonly string[]> = {
  policies: Object.values(PolicyStatus),
  clients: Object.values(ClientStatus),
  reimbursements: Object.values(ReimbursementStatus),
  "stoploss-claims": Object.values(StoplossClaimStatus),
  partners: Object.values(PartnerStatus),
};

interface PageHeaderProps {
  /** Dot-notation path to the record field used for the main title */
  titleKey?: string;
  /** Dot-notation path to the record field used for the subtitle */
  subtitleKey?: string;
  /** Dot-notation path to the status field for displaying/editing status */
  statusKey?: string;
  /** Dot-notation path to a field to display as a tag next to the title (e.g., "claimant_relationship") */
  titleTagKey?: string;
  /** Callback function for the action button */
  buttonAction?: () => void;
  /** Label text for the action button */
  buttonLabel?: string;
  /** Icon component to display before the button label */
  buttonIcon?: ReactNode;
  /** Custom content to render on the right side of the header */
  rightContent?: ReactNode;
  /** Icon to display on the left side of the header */
  headerIcon?: ReactNode;
  /** Override the resource name (defaults to current resource from context) */
  resourceName?: string;
  /** Fallback title if titleKey doesn't resolve */
  customTitle?: string;
  /** Fallback subtitle if subtitleKey doesn't resolve */
  customSubtitle?: string;
  /** Custom tag chip label to display next to the title (takes precedence over titleTagKey) */
  customTitleTag?: string;
  /** When set, the title tag chip links here (e.g. the policy page). #7581 AC3 */
  titleTagHref?: string;
  /**
   * Rich title (links, mixed weight) rendered in place of the title string. The
   * string title (customTitle / titleKey) still feeds the overflow tooltip and
   * the document title, so pass both. #7581.
   */
  titleNode?: ReactNode;
  /**
   * Rendered on the title row after the title tag, outside the h1 so it never
   * truncates the title: a record's context chip (e.g. the claimant's policy switcher).
   */
  titleAdornment?: ReactNode;
  /** Rich subtitle rendered in place of the subtitle string (links to related records). #7581 */
  subtitleNode?: ReactNode;
  /** Override the auto-generated section title */
  customSectionTitle?: string;
  /** Hide the section title completely */
  hideSectionTitle?: boolean;
  /** Allow status to be edited via dropdown (still requires edit permission) */
  enableStatusEdit?: boolean;
  /**
   * Optional hook run before a status change is sent. Return extra fields to merge
   * into the update, or null to abort (e.g. the user dismissed a dialog). Lets a
   * resource collect what a status needs — a cancelled policy needs its
   * termination_date (#7320).
   */
  prepareStatusChange?: (newStatus: string, record: RaRecord) => Promise<Record<string, unknown> | null>;
}

/**
 * PageHeader component for show/edit/create pages.
 */
export const PageHeader = ({
  titleKey = "",
  subtitleKey,
  statusKey,
  titleTagKey,
  buttonAction,
  buttonLabel = "View PDF",
  buttonIcon,
  rightContent,
  headerIcon,
  resourceName,
  customTitle = "Unknown",
  customSubtitle = "",
  customTitleTag,
  titleTagHref,
  titleNode,
  titleAdornment,
  subtitleNode,
  customSectionTitle,
  hideSectionTitle = true,
  enableStatusEdit = true,
  prepareStatusChange,
}: PageHeaderProps) => {
  const record = useRecordContext();
  const location = useLocation();
  const refresh = useRefresh();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const { theme } = useAppTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const contextResource = useResourceContext();
  const resolvedResourceName = resourceName ?? contextResource;

  // Check edit permissions for the current resource
  const { canEdit } = useResourcePermissions(resolvedResourceName as ResourceName);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Early return for show/edit pages without a record (allow create pages to render)
  if (!record && !location.pathname.includes("create") && titleKey !== "") return null;

  // Extract display values from the record
  const title = getNestedValue(record, titleKey, customTitle);
  const subtitle = subtitleKey !== undefined ? getNestedValue(record, subtitleKey, customSubtitle) : customSubtitle;
  const statusValue = statusKey ? getNestedValue(record, statusKey) : null;

  // Title tag (e.g., relationship or custom label like policy year)
  const titleTagValue = titleTagKey ? getNestedValue(record, titleTagKey, "") : null;
  const titleTagLabel =
    customTitleTag || (titleTagValue && titleTagValue !== "-" ? formatToTitleCase(titleTagValue as string) : null);

  // Format status for display chip
  const statusLabel = statusValue
    ? statusValue === "processing"
      ? "Processing..."
      : formatToTitleCase(statusValue as string)
    : null;
  const statusColor = statusValue ? getStatusColor(statusValue as StatusColor) : CommonStatus.DEFAULT;

  // Check if status editing is available for this resource
  const statusOptions = resolvedResourceName ? STATUS_OPTIONS[resolvedResourceName] || [] : [];
  // Include permission check - user must have edit permission to change status
  const canEditStatus = enableStatusEdit && canEdit && statusOptions.length > 0 && record?.id && resolvedResourceName;

  const handleStatusClick = (event: MouseEvent<HTMLDivElement>) => {
    if (canEditStatus) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!record?.id || !statusKey || !resolvedResourceName) return;

    let extraFields: Record<string, unknown> = {};
    if (prepareStatusChange) {
      const prepared = await prepareStatusChange(newStatus, record);
      if (prepared === null) {
        handleClose();
        return;
      }
      extraFields = prepared;
    }

    // TODO: WORKAROUND - Backend bug in PatchStoplossClaim converts all field types to strings
    // and clears required fields when sending partial updates. This sends the entire record
    // to prevent data loss. Fix the backend's PatchStoplossClaim handler to:
    // 1. Remove type conversion logic (lines 146-158 in handlers/stoploss_claim_handler.go)
    // 2. Use proper MongoDB $set operator for partial updates
    // Once fixed, change this to only send: { [statusKey]: newStatus }
    // The termination dialog can stay open for a while, and this PUT sends the whole
    // record. Re-read it right before writing so another user's edits made meanwhile
    // are not overwritten (same pattern as AccelFundingBanner, !4843 thread 29879).
    // Narrows the window to one round trip; closing it needs a version check the API
    // does not offer today.
    let fresh: RaRecord = record;
    try {
      const { data } = await dataProvider.getOne(resolvedResourceName, { id: record.id });
      fresh = data;
    } catch (error) {
      notifyError(error);
      handleClose();
      return;
    }
    const fullUpdate = {
      ...fresh,
      [statusKey]: newStatus,
      ...extraFields,
    };

    dataProvider
      .update(resolvedResourceName, {
        id: record.id,
        data: fullUpdate,
        previousData: fresh,
      })
      .then(() => {
        notify(`Status updated to ${newStatus}`, { type: AlertType.SUCCESS });
        refresh();
        handleClose();
      })
      .catch((error) => {
        notifyError(error);
        handleClose();
      });
  };

  // Build the resource display name
  const rawResourceName = resolvedResourceName || "record";
  const singularResource = depluralize(rawResourceName);
  const capitalizedResource = singularResource.charAt(0).toUpperCase() + singularResource.slice(1);

  // Determine page context from URL
  const viewType = location.pathname.includes("create")
    ? "create"
    : location.pathname.includes("edit")
      ? "edit"
      : "show";

  // Custom titles for specific resources
  const customResourceTitles: Record<string, string> = {
    "stoploss-claims": "Stop-Loss Claim",
  };

  const resolvedResource = customResourceTitles[rawResourceName] || capitalizedResource;

  const viewTitleMap: Record<string, string> = {
    create: `Create ${resolvedResource}`,
    edit: `Edit ${resolvedResource}`,
    show: `${resolvedResource} Details`,
  };

  const sectionTitle = (() => {
    // Special handling for vendors: show specific vendor type instead of generic "Vendor"
    if (rawResourceName === ResourceName.VENDORS && record) {
      const types: string[] = [];
      if (record.is_sl_carrier) types.push("Carrier");
      if (record.is_tpa) types.push("TPA");
      if (record.is_rx) types.push("Rx Vendor");

      if (types.length > 0) {
        const vendorType = types.join(" / ");
        return viewType === "show"
          ? `${vendorType} Details`
          : viewType === "edit"
            ? `Edit ${vendorType}`
            : `Create ${vendorType}`;
      }
    }

    // Default behavior for all other resources
    return viewTitleMap[viewType] || `${capitalizedResource}`;
  })();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        justifyContent: "space-between",
        alignItems: isMobile ? "flex-start" : "center",
        gap: isMobile ? 2 : 0,
        mb: 1,
        bgcolor: "background.paper",
        px: isMobile ? 3 : 5,
        py: isMobile ? 2 : 3,
        borderBottom: "1px solid",
        borderColor: "divider",
        borderRadius: "0 0 16px 16px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
          minWidth: 0,
          flex: 1,
        }}
      >
        {/* Optional header icon */}
        {headerIcon && (
          <Box
            sx={{
              p: 1.5,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {headerIcon}
          </Box>
        )}

        <Box sx={{ minWidth: 0, flex: 1 }}>
          {/* Section title (e.g., "Client Details", "Carrier Details") */}
          {!hideSectionTitle && (
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{
                fontWeight: 300,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {customSectionTitle ? customSectionTitle : sectionTitle}
            </Typography>
          )}

          {/* Title row: title + tag + status */}
          {title && (
            <Box
              sx={{
                display: "flex",
                alignItems: isMobile ? "flex-start" : "center",
                flexDirection: isMobile ? "column" : "row",
                gap: 1.5,
              }}
            >
              {/* Title and title tag grouped together */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                  minWidth: 0,
                }}
              >
                <Tooltip
                  title={typeof title === "string" && title.length > 60 ? title : ""}
                  placement="top"
                  arrow
                  disableHoverListener={typeof title !== "string" || title.length <= 60}
                >
                  <Typography
                    variant="h1"
                    fontWeight="bold"
                    sx={{
                      maxWidth: isMobile ? "100%" : "600px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: isMobile ? "1.75rem" : undefined, // Slightly smaller on mobile
                    }}
                  >
                    {titleNode ?? title}
                  </Typography>
                </Tooltip>

                {/* Title tag chip (e.g., relationship) */}
                {titleTagLabel &&
                  (titleTagHref ? (
                    <Box
                      component={RouterLink}
                      to={titleTagHref}
                      aria-label={titleTagLabel}
                      sx={{
                        display: "inline-flex",
                        textDecoration: "none",
                        "&:hover .MuiChip-root": { opacity: 0.85 },
                      }}
                    >
                      <StatusChip label={titleTagLabel} color={ColorType.Default} />
                    </Box>
                  ) : (
                    <StatusChip label={titleTagLabel} color={ColorType.Default} />
                  ))}
                {titleAdornment}
              </Box>

              {/* Status chip with optional edit dropdown */}
              {statusKey && statusLabel && (
                <Box
                  onClick={handleStatusClick}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    cursor: canEditStatus ? "pointer" : "default",
                    "&:hover .status-arrow": {
                      opacity: canEditStatus ? 1 : 0,
                    },
                  }}
                >
                  <StatusChip label={statusLabel} color={statusColor} />
                  {canEditStatus && (
                    <KeyboardArrowDownIcon
                      className="status-arrow"
                      sx={{
                        opacity: 0,
                        transition: "opacity 0.2s",
                        fontSize: "1.2rem",
                        color: "text.secondary",
                      }}
                    />
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Subtitle - separate from title row with consistent spacing */}
          {(subtitleNode || (subtitle && subtitle !== "-")) && (
            <Typography
              variant="body1"
              color="text.secondary"
              component="div"
              sx={{
                mt: 0.5,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {subtitleNode ?? subtitle}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Right side content: action button and/or custom content */}
      {(buttonAction || rightContent) && (
        <Box
          sx={{
            display: "flex",
            alignItems: isMobile ? "flex-start" : "center",
            width: isMobile ? "100%" : "auto",
            justifyContent: isMobile ? "flex-start" : "flex-start",
            mt: isMobile ? 1 : 0,
          }}
        >
          {buttonAction && <GhostButton label={buttonLabel} onClick={buttonAction} startIcon={buttonIcon} />}
          {rightContent}
        </Box>
      )}

      {/* Status dropdown menu - rendered at root level to avoid z-index issues */}
      {canEditStatus && (
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          {statusOptions.map((statusOption) => (
            <MenuItem
              key={statusOption}
              onClick={() => handleStatusChange(statusOption)}
              selected={statusOption === statusValue}
            >
              {formatToTitleCase(statusOption)}
            </MenuItem>
          ))}
        </Menu>
      )}
    </Box>
  );
};
